import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import {
  installPatchDevices,
  multiPatchDevicesQuery,
  multiPatchesTableQuery,
  uninstallPatchDevices,
  type MultiPatchKind,
  type MultiPatchRow,
} from '../api/opteemum'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'

/**
 * Fila „OS Patch Management" din widgetul „Multiple devices" -
 * `Opteemum::multipleOsPatchManagement` (`multipleOsPatchManagement.php`):
 * trei file (Missing / Installed / Hidden), fiecare cu tabelul
 * `companyPatchesTableData` - un rand pe petec, cu numarul de masini - si,
 * la plus, randul desfacut din `get*PatchDevices`: masinile firmei cu petecul
 * intr-un `selectpicker` multiplu si butonul Install / Uninstall
 * (`missingPatchDevices.php`, `installedPatchDevices.php`,
 * `hiddenPatchDevices.php`). Trimiterea merge in `installPatchDevices` /
 * `uninstallPatchDevices`, o comanda pe fiecare masina aleasa.
 *
 * Acelasi markup si aceleasi clase ca portalul (`line-tabs`, `dataTable`,
 * `details_content`, `labelcustomize`); tabelul e pe toata firma, ca in vechi.
 */

const TABS: Array<{ kind: MultiPatchKind; icon: string; label: string }> = [
  { kind: 'missing', icon: 'fas fa-exclamation-circle', label: 'Missing Patches' },
  { kind: 'installed', icon: 'fa fa-cube', label: 'Installed Patches' },
  { kind: 'hidden', icon: 'fa fa-eye-slash', label: 'Hidden Patches' },
]

const COLUMNS: Array<{ title: string; orderable: boolean; width?: number }> = [
  { title: '', orderable: false, width: 50 },
  { title: 'Name', orderable: true, width: 200 },
  { title: 'Severity', orderable: true },
  { title: 'Size', orderable: true, width: 100 },
  { title: 'Require source', orderable: true, width: 100 },
  { title: 'Mandatory', orderable: true, width: 100 },
  { title: 'Beta', orderable: true, width: 100 },
  { title: 'Reboot behaviour', orderable: true, width: 100 },
  { title: 'Devices', orderable: true, width: 100 },
]

function DevicesForm({
  patch,
  kind,
  label,
  install,
  devices,
  onDone,
}: {
  patch: number
  kind: MultiPatchKind
  label: string
  install: boolean
  devices: Array<{ id: number; name: string }>
  onDone(): void
}) {
  const [picked, setPicked] = useState<string[]>(() => devices.map((d) => String(d.id)))
  const [busy, setBusy] = useState(false)

  const submit = async (ev: FormEvent) => {
    ev.preventDefault()
    if (busy) return
    const ids = picked.map(Number).filter((n) => n > 0)
    if (ids.length === 0) {
      toast('Select at least one device.', 'Error', 'error')
      return
    }
    if (!window.confirm('Are you sure?')) return
    setBusy(true)
    try {
      const done = install ? await installPatchDevices(patch, ids) : await uninstallPatchDevices(patch, ids)
      if (done.ok) {
        toast(`${install ? 'Install' : 'Uninstall'} command sent.`, 'Success', 'success')
        onDone()
      } else toast(done.message ?? 'An error occured! Please try again..', 'Error', 'error')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'An error occured! Please try again..', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      className="form-horizontal fv-form fv-form-bootstrap labelcustomize"
      id={install ? 'installPatch' : 'uninstallPatch'}
      method="post"
      onSubmit={submit}
    >
      <div className="form-group col-md-9">
        <label className="col-md-3 control-label" style={{ textAlign: 'left' }}>
          <span className="color">{label}</span>
        </label>
        <div className="col-md-9 selectContainer">
          <SelectPicker
            id={`patch-${kind}-${patch}-${install ? 'install' : 'uninstall'}`}
            name="devices[]"
            title="Nothing selected"
            multiple
            size={10}
            selectedTextFormat="count"
            options={devices.map((d) => ({ value: String(d.id), label: d.name }))}
            value={picked}
            onChange={setPicked}
          />
        </div>
      </div>
      <div className="col-md-3">
        <button type="submit" className={`btn ${install ? 'btn-primary installPatch' : 'btn-danger uninstallPatch'} btn-sm`} disabled={busy}>
          {install ? 'Install' : 'Uninstall'}
        </button>
      </div>
    </form>
  )
}

function PatchDevices({ kind, patch, onDone }: { kind: MultiPatchKind; patch: number; onDone(): void }) {
  const query = useQuery(multiPatchDevicesQuery(kind, patch))
  if (query.isPending) return <div className="loading">Loading...</div>
  if (query.isError) return <div className="alert alert-danger">{query.error.message}</div>
  const { missing, installed } = query.data
  if (missing.length === 0 && installed.length === 0) {
    return (
      <div className="details_content">
        <div className="col-md-12">
          <div className="alert alert-danger" role="alert">
            <p>No devices with this patch</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="details_content">
      <div className="col-md-12">
        {missing.length > 0 ? (
          <DevicesForm
            patch={patch}
            kind={kind}
            install
            label={kind === 'hidden' ? 'Devices with missing patch' : 'Devices'}
            devices={missing}
            onDone={onDone}
          />
        ) : null}
        {installed.length > 0 ? (
          <DevicesForm
            patch={patch}
            kind={kind}
            install={false}
            label={kind === 'hidden' ? 'Devices with installed patch' : 'Devices'}
            devices={installed}
            onDone={onDone}
          />
        ) : null}
      </div>
    </div>
  )
}

export function MultiOsPatchesTab() {
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<MultiPatchKind>('missing')
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [search, setSearch] = useState('')
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 0, dir: 'asc' })
  const [open, setOpen] = useState<number | null>(null)

  const table = useQuery({
    ...multiPatchesTableQuery({ kind, start, length, search, orderColumn: order.column, orderDir: order.dir }),
    placeholderData: keepPreviousData,
  })

  const pick = (next: MultiPatchKind) => {
    setKind(next)
    setStart(0)
    setSearch('')
    setOrder({ column: 0, dir: 'asc' })
    setOpen(null)
  }
  const refresh = () => {
    setOpen(null)
    void queryClient.invalidateQueries({ queryKey: ['opteemum', 'multi', 'os-patches'] })
  }

  const rows: MultiPatchRow[] = table.data?.data ?? []
  const total = table.data?.recordsFiltered ?? 0
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const id = `multiple${kind[0].toUpperCase()}${kind.slice(1)}Patches`

  return (
    <div className="row">
      <div className="col-md-12">
        <ul className="nav nav-tabs line-tabs mb-2 patchList" role="tablist">
          {TABS.map((t) => (
            <li key={t.kind} className={t.kind === kind ? 'active' : undefined}>
              <a
                href={`/api/v2/opteemum/devices/multi/os-patches/table?kind=${t.kind}`}
                onClick={(e) => {
                  e.preventDefault()
                  pick(t.kind)
                }}
              >
                <i className={t.icon} aria-hidden="true"></i> {t.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${id}_wrapper`}>
            <div className="row">
              <div className="col-sm-6">
                <div className="dataTables_length" id={`${id}_length`}>
                  <label>
                    Show{' '}
                    <select
                      className="form-control input-sm"
                      value={length}
                      onChange={(e) => {
                        setLength(Number(e.target.value))
                        setStart(0)
                      }}
                    >
                      {LENGTHS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>{' '}
                    entries
                  </label>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="dataTables_filter" id={`${id}_filter`}>
                  <label>
                    Search:{' '}
                    <input
                      type="search"
                      className="form-control input-sm"
                      placeholder=""
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value)
                        setStart(0)
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <div className="dataTables_processing" style={{ display: table.isFetching ? 'block' : 'none' }}>
                  Processing...
                </div>
                <table className={`${kind}-patches table table-striped table-hover dataTable no-footer`} width="100%" id={id} role="grid">
                  <thead>
                    <tr role="row">
                      {COLUMNS.map((c, i) => {
                        const sorted = order.column === i
                        const cls = c.orderable ? (sorted ? `sorting_${order.dir}` : 'sorting') : 'sorting_disabled'
                        return (
                          <th
                            key={i}
                            className={cls}
                            style={c.width ? { width: c.width } : undefined}
                            onClick={() => {
                              if (!c.orderable) return
                              setOrder({ column: i, dir: sorted && order.dir === 'asc' ? 'desc' : 'asc' })
                            }}
                          >
                            {c.title}
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {table.isError ? (
                      <tr>
                        <td colSpan={COLUMNS.length} className="dataTables_empty">
                          {table.error.message}
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={COLUMNS.length} className="dataTables_empty">
                          {table.isPending ? 'Loading...' : 'No data available in table'}
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <RowPair key={r.DT_RowId} row={r} kind={kind} open={open === r.patchId} onToggle={() => setOpen(open === r.patchId ? null : r.patchId)} onDone={refresh} />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-5">
                <div className="dataTables_info" id={`${id}_info`} role="status" aria-live="polite">
                  Showing {total === 0 ? 0 : start + 1} to {Math.min(start + length, total)} of {total} entries
                </div>
              </div>
              <div className="col-sm-7">
                <div className="dataTables_paginate paging_simple_numbers" id={`${id}_paginate`}>
                  <ul className="pagination">
                    <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          if (page > 0) setStart((page - 1) * length)
                        }}
                      >
                        Previous
                      </a>
                    </li>
                    {pageButtons(page, pages).map((b, i) =>
                      b === 'ellipsis' ? (
                        <li key={`e${i}`} className="paginate_button disabled">
                          <a href="#" onClick={(e) => e.preventDefault()}>
                            …
                          </a>
                        </li>
                      ) : (
                        <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault()
                              setStart(b * length)
                            }}
                          >
                            {b + 1}
                          </a>
                        </li>
                      ),
                    )}
                    <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          if (page < pages - 1) setStart((page + 1) * length)
                        }}
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Randul din tabel + randul desfacut de sub el (`show_details` din opteemum.js). */
function RowPair({
  row,
  kind,
  open,
  onToggle,
  onDone,
}: {
  row: MultiPatchRow
  kind: MultiPatchKind
  open: boolean
  onToggle(): void
  onDone(): void
}) {
  return (
    <>
      <tr role="row" id={row.DT_RowId} className={open ? 'shown' : undefined}>
        <td>
          <center className="show_details color-dark-grey pointer font-16" data-id={row.patchId} onClick={onToggle}>
            <i className={open ? 'fas fa-minus-circle' : 'fas fa-plus-circle'}></i>
          </center>
        </td>
        <td className="td-small a-left">
          {row.description ? (
            <>
              <span data-toggle="tooltip" data-placement="top" title={row.description}>
                <i className="fas fa-info-circle color-darkblue font-14"></i>
              </span>{' '}
            </>
          ) : null}
          {row.title}
        </td>
        <td className="td-small a-left">{row.severity}</td>
        <td className="td-small a-left">{row.size}</td>
        <td className="td-small a-left" dangerouslySetInnerHTML={{ __html: row.require_source }} />
        <td className="td-small a-left" dangerouslySetInnerHTML={{ __html: row.mandatory }} />
        <td className="td-small a-left" dangerouslySetInnerHTML={{ __html: row.beta }} />
        <td className="td-small a-left">{row.reboot}</td>
        <td className="td-small a-left">
          <span className="label label-primary">{row.devices}</span>
        </td>
      </tr>
      {open ? (
        <tr className="details">
          <td colSpan={COLUMNS.length}>
            <PatchDevices kind={kind} patch={row.patchId} onDone={onDone} />
          </td>
        </tr>
      ) : null}
    </>
  )
}
